import { InputHTMLAttributes } from 'react'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helpText?: string
  fullWidth?: boolean
  prefix?: string
}

/**
 * Reusable input component with validation support
 * Mobile-first design with 16px minimum font size to prevent iOS zoom
 */
export function Input({
  label,
  error,
  helpText,
  fullWidth = true,
  prefix,
  className = '',
  ...props
}: InputProps) {
  const inputId = props.id || `input-${Math.random().toString(36).substring(2, 11)}`

  const inputStyles = {
    display: 'block',
    width: fullWidth ? '100%' : 'auto',
    // FR-047: Minimum 44px height for touch target
    minHeight: '44px',
    // Prevent iOS zoom on focus
    fontSize: '16px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: prefix ? '12px 16px 12px 48px' : '12px 16px',
    border: `2px solid ${error ? '#dc3545' : '#ddd'}`,
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    backgroundColor: '#fff',
  }

  const labelStyles = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const errorStyles = {
    display: 'block',
    marginTop: '6px',
    fontSize: '14px',
    color: '#dc3545',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const helpTextStyles = {
    display: 'block',
    marginTop: '6px',
    fontSize: '14px',
    color: '#666',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const inputWrapperStyles = {
    position: 'relative' as const,
    width: fullWidth ? '100%' : 'auto',
  }

  const prefixStyles = {
    position: 'absolute' as const,
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '16px',
    color: '#333',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    pointerEvents: 'none' as const,
  }

  return (
    <div style={{ marginBottom: '20px', width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label htmlFor={inputId} style={labelStyles}>
          {label}
        </label>
      )}

      <div style={inputWrapperStyles}>
        {prefix && <span style={prefixStyles}>{prefix}</span>}
        <input
          id={inputId}
          style={inputStyles}
          onFocus={e => {
            if (!error) {
              e.currentTarget.style.borderColor = '#007AFF'
            }
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = error ? '#dc3545' : '#ddd'
          }}
          className={className}
          {...props}
        />
      </div>

      {error && <span style={errorStyles}>{error}</span>}
      {helpText && !error && <span style={helpTextStyles}>{helpText}</span>}
    </div>
  )
}
